// @ts-ignore
import React, { Fragment, useCallback, useEffect } from 'react';
import { Box } from 'theme-ui';
import TransitionTool from './toolPanel/TransitionTool';
import { useDispatch, useSelector } from 'react-redux';
import { ActiveTool } from 'types/AppState';
import { getActiveTool, setActiveTool } from 'modules/activeTool';
import { RatioFormat } from './toolPanel/RatioFormatTool';
import { TextTool } from './toolPanel/TextTool';
import Scroll from 'shared/Scroll';
import { CloseTag } from 'shared/CloseTag';
import { getActiveEffects } from 'modules/effects';
import { AudioTool } from './toolPanel/AudioTool';
import { RemoveWatermarkPopup } from './toolPanel/RemoveWatermarkPopup';
import { PhotoSidebarTool } from './toolPanel/PhotoSidebarTool';
import { TemplatesTool } from './toolPanel/TemplatesTool';
import { addURLParameters, removeURLParameters } from 'utils/directLinks';
import { getRatioFormat } from 'modules/ratioFormat';
import { directLinkedTools } from 'constants/directLinks';
import { isEmpty } from 'ramda';
import { closeSidebar, isSidebarOpen, openSidebar, toggleSidebar } from 'modules/sidebar';
import { Sidebar } from 'types/Sidebar';
import { StockVideosTool } from './toolPanel/StockVideosTool';
import { TextSettingsTool } from './toolPanel/TextSettings';
import TextColorPicker from './toolPanel/TextColorPicker';
import TextOutline from './toolPanel/TextControls';
import { usePlayer } from 'utils/PlayerContext';

export const ToolPanel = () => {
  const activeTool = useSelector(getActiveTool);
  const isOpen = useSelector(isSidebarOpen(Sidebar.ToolPanel));
  const effect = useSelector(getActiveEffects);
  const selectedRatio = useSelector(getRatioFormat);
  const dispatch = useDispatch();
  const { pause } = usePlayer();

  // If TextEffect removed from scene fallback to text tool
  useEffect(() => {
    if ([ActiveTool.Font, ActiveTool.Color].includes(activeTool) && isEmpty(effect)) {
      dispatch(setActiveTool(ActiveTool.Text));
    }
  }, [activeTool, effect]);

  useEffect(() => {
    pause();
    changeSidebarStateHandler(activeTool !== ActiveTool.None);
  }, [activeTool, pause]);

  useEffect(() => {
    if (!directLinkedTools.includes(activeTool as string)) {
      removeURLParameters(['tool', 'fit']);
    } else {
      addURLParameters({
        tool: activeTool as string,
        fit: activeTool === ActiveTool.RatioFormat ? selectedRatio : null
      });
    }
  }, [activeTool]);

  const toggleSidebarHandler = useCallback(() => {
    dispatch(toggleSidebar(Sidebar.ToolPanel));
  }, []);

  const changeSidebarStateHandler = useCallback((state: boolean) => {
    dispatch(state ? openSidebar(Sidebar.ToolPanel) : closeSidebar(Sidebar.ToolPanel));
  }, []);

  return (
    <Fragment>
      <Box
        sx={{
          flex: '0 0 360px',
          position: 'relative',
          display: isOpen ? 'block' : 'none',
          zIndex: '10',
          marginLeft: '4px'
        }}
        data-test="tool-panel"
      >
        <div
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: '8px',
            bg: 'background',
            overflow: 'hidden'
          }}
        >
          {activeTool === ActiveTool.Color && (
            <Scroll>
              <Box sx={{ p: 20 }} data-test="tool-panel_text-color">
                <TextColorPicker mode="textColor" />
                <TextOutline mode="text" />
              </Box>
            </Scroll>
          )}
          {activeTool === ActiveTool.Outline && (
            <Scroll>
              <Box sx={{ p: 20 }} data-test="tool-panel_outline">
                <TextColorPicker mode="outlineColor" />
                <TextOutline mode="outline" />
              </Box>
            </Scroll>
          )}
          {activeTool === ActiveTool.Shadow && (
            <Scroll>
              <Box sx={{ p: 20 }} data-test="tool-panel_shadow">
                <TextColorPicker mode="shadowColor" />
                <TextOutline mode="shadow" />
              </Box>
            </Scroll>
          )}
          {activeTool === ActiveTool.Transition && (
            <Scroll>
              <Box sx={{ p: 20 }} data-test="tool-panel_transition">
                <TransitionTool />
              </Box>
            </Scroll>
          )}
          {activeTool === ActiveTool.RatioFormat && <RatioFormat />}
          {activeTool === ActiveTool.Text && (
            <Box sx={{ overflow: 'hidden', height: '100%' }}>
              <TextTool />
            </Box>
          )}
          {activeTool === ActiveTool.PhotoSidebar && (
            <div data-test="tool-panel_photo" sx={{ height: '100%' }}>
              <PhotoSidebarTool />
            </div>
          )}
          {activeTool === ActiveTool.StockVideos && (
            <div data-test="tool-panel_stock-videos" sx={{ height: '100%' }}>
              <StockVideosTool />
            </div>
          )}
          {activeTool === ActiveTool.Font && !isEmpty(effect) && (
            <div>
              <div
                sx={{
                  marginTop: 14
                }}
                data-test="tool-panel_text-font"
              >
                <TextSettingsTool />
              </div>
            </div>
          )}
          {activeTool === ActiveTool.Audio && (
            <Box sx={{ pt: 20, pl: 20, pr: 20 }} data-test="tool-panel_audio">
              <AudioTool />
            </Box>
          )}
          {activeTool === ActiveTool.Templates && (
            <div data-test="tool-panel_templates" sx={{ height: '100%' }}>
              <TemplatesTool />
            </div>
          )}
        </div>
      </Box>
      <CloseTag
        isOpen={isOpen}
        sx={{
          left: 447,
          bottom: 174,
          marginLeft: isOpen ? '-4px' : '-368px',
          zIndex: 'closeTag'
        }}
        handleClick={() => toggleSidebarHandler()}
      />
      <RemoveWatermarkPopup
        sx={{
          marginLeft: isOpen ? 0 : '-367px'
        }}
      />
    </Fragment>
  );
};
